import { Box, Button, TextField, Typography, Alert } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import themeColors from "../theme";
import Logo from "../components/Logo";

export default function Registration() {
    const navigate = useNavigate();
    const { teal, darkTeal, lightGray } = themeColors;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const validatePassword = (value) => {
        const lengthOk = value.length >= 8;
        const hasUpper = /[A-Z]/.test(value);
        // Match any non-alphanumeric character (special character)
        const hasSpecial = /[^a-zA-Z0-9]/.test(value);

        if (!lengthOk || !hasUpper || !hasSpecial) {
            return "Password must be at least 8 characters, include an uppercase letter and a special character.";
        }
        return "";
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        // Only validate if password is not empty
        if (value.length > 0) {
            setPasswordError(validatePassword(value));
        } else {
            setPasswordError("");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        const validationMsg = validatePassword(password);
        if (validationMsg) {
            setPasswordError(validationMsg);
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/register`, { email, password });
            setRegistrationSuccess(true);
        } catch (err) {
            setError(err.response?.data?.error || "Registration failed.");
        }
    };

    // Show success message after registration
    if (registrationSuccess) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: lightGray,
                    px: 2,
                }}
            >
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Logo />
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 470,
                        backgroundColor: "#fff",
                        p: 5,
                        borderRadius: 2,
                        boxShadow: 3,
                        textAlign: "center",
                    }}
                >
                    <MailOutlineIcon sx={{ fontSize: 64, color: teal, mb: 2 }} />

                    <Typography variant="h4" fontWeight="600" mb={2} sx={{ color: "#1D3557" }}>
                        Check Your Email
                    </Typography>

                    <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                        We've sent a verification link to <strong>{email}</strong>
                    </Alert>

                    <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                        Please click the link in the email to verify your account and activate it. The link will expire in 24 hours.
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#5A6C7D", mb: 3 }}>
                        Didn't receive the email? Check your spam folder or{" "}
                        <Button
                            variant="text"
                            sx={{ color: teal, textTransform: "none", p: 0, minWidth: "auto", fontWeight: 500 }}
                            onClick={() => navigate("/email-not-verified", { state: { email } })}
                        >
                            request a new one
                        </Button>
                    </Typography>

                    <Box sx={{ borderTop: "1px solid #E0E0E0", pt: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate("/login")}
                            sx={{
                                py: 1.5,
                                backgroundColor: teal,
                                '&:hover': { backgroundColor: darkTeal },
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.95rem"
                            }}
                        >
                            Go to Login
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lightGray,
                px: 2,
            }}
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Logo/>
            </Box>

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: "100%",
                    maxWidth: 470,
                    backgroundColor: "#fff",
                    p: 5,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                <Typography variant="h4" fontWeight="600" mb={2} sx={{ color: "#1D3557" }}>
                    Sign Up
                </Typography>

                <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                    Create your free account to track CME funds,
                    <br />
                    credits, and documents.
                </Typography>

                <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: false }}
                />

                <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Password"
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!passwordError}
                    sx={{ mb: 0.5 }}
                    InputLabelProps={{ shrink: false }}
                />

                <Typography variant="caption" sx={{ display: "block", textAlign: "left", color: "#5A6C7D", mb: 2 }}>
                    Minimum 8 characters, 1 uppercase letter, 1 special character
                </Typography>

                {passwordError && (
                    <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                        {passwordError}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Confirm password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: false }}
                />

                {!email || !password || !confirmPassword ? (
                    <Typography variant="body2" sx={{ color: "#5A6C7D", mb: 2 }}>
                        Complete all fields to continue
                    </Typography>
                ) : null}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                        mt: 1,
                        mb: 2,
                        py: 1.5,
                        backgroundColor: teal,
                        '&:hover': { backgroundColor: darkTeal },
                        textTransform: "uppercase",
                        fontWeight: 600,
                        fontSize: "0.95rem"
                    }}
                    disabled={!email || !password || !confirmPassword || !!passwordError || password !== confirmPassword}
                >
                    SIGN UP
                </Button>

                <Box sx={{ borderTop: "1px solid #E0E0E0", pt: 2 }}>
                    <Typography variant="body2" sx={{ color: "#5A6C7D" }}>
                        Already have an account?{" "}
                        <Button
                            variant="text"
                            sx={{ color: teal, textTransform: "none", p: 0, minWidth: "auto", fontWeight: 500 }}
                            onClick={() => navigate("/login")}
                        >
                            Sign in
                        </Button>
                    </Typography>
                </Box>

                <Typography variant="caption" sx={{ display: "block", color: "#5A6C7D", mt: 3 }}>
                    Your information is encrypted and never shared.
                </Typography>
            </Box>
        </Box>
    );
}
